import { CardActionArea, Typography, useTheme } from '@mui/material'
import {
    FlexBox,
    FlexBoxColumn,
    FlexBoxSpaceBetween,
    HorseButton,
    SortableWrapper,
    StakeInput,
    SymbolButton
} from 'components/atoms'
import { useBetContext } from 'contexts/BetContext'
import { useTranslation } from 'contexts/TranslationContext'
import { TrashIcon } from 'icons'
import { ActionType, BETS_CONFIG } from 'models'
import { useEffect, useMemo, useState } from 'react'
import { ReactSortable } from 'react-sortablejs'
import { Fragment } from 'react/jsx-runtime'
import { getBetDesignation, parseBetDesignation, pluralize } from 'utils'

export type SortableItemType = {
    id: number
    base: string
}

export const RunnerBet = ({ betIds }: { betIds: string[] }) => {
    const theme = useTheme()
    const [deleted, setDeleted] = useState(false)
    const [sortedBases, setSortedBases] = useState<SortableItemType[]>([])
    const translation = useTranslation()

    const {
        selectedBetType,
        setRunnerSelectionVisible,
        removeHorseSelection,
        setBetStake,
        setBetDesignation,
        selectedFormulaKey,
        selectedBets,
        availableRunners,
        handleTrackingEvent,
        betError,
        setBetError,
        focusedInputId,
        setFocusedInputId
    } = useBetContext()

    const betWin = selectedBets.get(betIds[0])
    const betPlace = selectedBets.get(betIds[1])

    const showX = selectedBetType !== 'S' && selectedFormulaKey !== 'UNIT'
    const hasWinOrPlaceType = Boolean(BETS_CONFIG[selectedBetType]?.types)

    const handleClick = (actionType: ActionType) => {
        setRunnerSelectionVisible(true)
        void handleTrackingEvent({
            eventType: 'BET',
            actionType
        })
    }

    const { base, associates } = useMemo(
        () => parseBetDesignation(betWin?.designation),
        [betWin?.designation]
    )

    useEffect(() => {
        setBetError(
            Boolean(
                selectedBetType === 'C' &&
                    betWin?.stake &&
                    betWin.stake > 0 &&
                    betPlace?.stake &&
                    betPlace.stake > 0 &&
                    betWin.stake !== betPlace.stake
            )
        )
    }, [betWin, betPlace])

    // Update sortedBases when base changes from context
    useEffect(() => {
        const sorted = sortedBases.map((base) => base.base)
        if (base.join() !== sorted.join()) {
            setSortedBases(base.map((base, index) => ({ id: index, base })))
        }
    }, [base])

    // Update bet designation when sortedBases changes from user interaction
    useEffect(() => {
        const sorted = sortedBases.map((base) => base.base)
        if (base.join() !== sorted.join() && sortedBases.length && betWin) {
            const newDesignation = getBetDesignation({
                base: sorted,
                associates,
                formulaKey: selectedFormulaKey,
                betType: selectedBetType
            })
            setBetDesignation(betWin.id, newDesignation)
        }
    }, [sortedBases])

    if (!betWin) return null
    return (
        <FlexBoxSpaceBetween
            sx={{
                width: `calc(100% - ${theme.spacing(4)})`,
                px: 2,
                py: 1.5,
                backgroundColor: '#F4F6FA',
                alignItems: showX ? 'flex-start' : 'center',
                position: 'relative',
                left: deleted ? '-100vw' : 0,
                transition: 'left 300ms ease-in-out'
            }}
        >
            <FlexBoxColumn sx={{ alignItems: 'flex-start', mr: 2, flex: 1 }}>
                {selectedFormulaKey !== 'UNIT' && selectedBetType !== 'S' && (
                    <Typography
                        variant="text-12-semi-bold"
                        sx={{ color: theme.palette.text.secondary, mb: 0.5 }}
                    >
                        {translation.base}
                    </Typography>
                )}
                <ReactSortable
                    list={sortedBases}
                    setList={setSortedBases}
                    group={'baseGroup'}
                    sort={BETS_CONFIG[selectedBetType]?.order}
                    animation={500}
                    delayOnTouchOnly={true}
                    delay={10}
                    tag={SortableWrapper}
                >
                    {sortedBases.length
                        ? sortedBases.map(
                              ({ id, base: horseNumber }, index) => (
                                  <Fragment
                                      key={`runner-bet-${id}-${horseNumber}-${index}`}
                                  >
                                      {horseNumber === 'X' ? (
                                          <SymbolButton
                                              symbol={showX ? 'X' : '+'}
                                              onClick={() => handleClick('ADD')}
                                          />
                                      ) : (
                                          <HorseButton
                                              onClick={() =>
                                                  handleClick('CHANGE')
                                              }
                                              horseNumber={horseNumber}
                                          />
                                      )}
                                      {horseNumber !== 'X' &&
                                          index + 1 === base.length &&
                                          selectedFormulaKey === 'UNIT' &&
                                          selectedBetType !== 'S' &&
                                          base.length <
                                              availableRunners.length && (
                                              <SymbolButton
                                                  symbol={'+'}
                                                  onClick={() =>
                                                      handleClick('ADD')
                                                  }
                                              />
                                          )}
                                      {betWin.odd ? (
                                          <Typography
                                              variant="title-14-bold"
                                              sx={{
                                                  color: '#434B5A',
                                                  width: 40,
                                                  textAlign: 'center'
                                              }}
                                          >
                                              {betWin.odd
                                                  .toString()
                                                  .replace('.', ',')}
                                          </Typography>
                                      ) : null}
                                  </Fragment>
                              )
                          )
                        : null}
                </ReactSortable>
                {selectedFormulaKey === 'PART_WHEEL' &&
                    selectedBetType !== 'S' && (
                        <>
                            <Typography
                                variant="text-12-semi-bold"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    mt: 1,
                                    mb: 0.5
                                }}
                            >
                                {pluralize(
                                    translation.associate,
                                    associates.length
                                )}
                            </Typography>
                            <FlexBox
                                sx={{
                                    mr: 1,
                                    mb: 0.5,
                                    width: '100%',
                                    flexWrap: 'wrap',
                                    gap: 1
                                }}
                            >
                                {associates.map((horseNumber, index) => (
                                    <Fragment
                                        key={`runner-bet-${betWin.id}-${horseNumber}-associate`}
                                    >
                                        <HorseButton
                                            horseNumber={horseNumber}
                                            onClick={() =>
                                                handleClick('CHANGE')
                                            }
                                        />
                                        {associates.length === index + 1 && (
                                            <SymbolButton
                                                symbol="+"
                                                onClick={() =>
                                                    handleClick('ADD')
                                                }
                                            />
                                        )}
                                    </Fragment>
                                ))}
                                {associates.length === 0 && (
                                    <SymbolButton
                                        symbol="+"
                                        onClick={() => handleClick('ADD')}
                                    />
                                )}
                            </FlexBox>
                        </>
                    )}
            </FlexBoxColumn>
            <FlexBoxColumn>
                <FlexBox sx={{ mt: showX ? 2 : 0 }}>
                    <StakeInput
                        bet={betWin}
                        error={betError}
                        label={
                            hasWinOrPlaceType
                                ? translation.winBet
                                : translation.stake
                        }
                        focused={focusedInputId === betWin.id}
                        onChange={(e) => {
                            setBetStake(betWin.id, +e.target.value)
                        }}
                        onBlur={() => {
                            betWin.stake &&
                                handleTrackingEvent({
                                    eventType: 'BET',
                                    actionType: hasWinOrPlaceType
                                        ? 'WIN'
                                        : 'STAKE'
                                })
                        }}
                        onMouseDown={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setFocusedInputId(betWin.id)
                        }}
                    />
                    {betPlace && hasWinOrPlaceType && (
                        <StakeInput
                            bet={betPlace}
                            error={betError}
                            label={translation.placeBet}
                            focused={focusedInputId === betPlace.id}
                            onChange={(e) => {
                                setBetStake(betPlace.id, +e.target.value)
                            }}
                            onBlur={() => {
                                betPlace.stake &&
                                    handleTrackingEvent({
                                        eventType: 'BET',
                                        actionType: 'PLACE'
                                    })
                            }}
                            onMouseDown={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setFocusedInputId(betPlace.id)
                            }}
                        />
                    )}
                </FlexBox>
                {betError && (
                    <Typography
                        color="error"
                        sx={{ fontSize: 12, mt: 0.5, mb: -1 }}
                    >
                        {translation.identicalBets}
                    </Typography>
                )}
            </FlexBoxColumn>
            {selectedBetType === 'S' && (
                <CardActionArea
                    sx={{ width: 'fit-content', ml: 2 }}
                    onClick={() => {
                        setDeleted(true)
                        setTimeout(() => {
                            removeHorseSelection(base[0])
                            void handleTrackingEvent({
                                eventType: 'BET',
                                actionType: 'REMOVE'
                            })
                        }, 300)
                    }}
                >
                    <TrashIcon />
                </CardActionArea>
            )}
        </FlexBoxSpaceBetween>
    )
}
